/**
 * DateAvailabilityChecker ✦ iter224
 *
 * The customer's first proper booking surface. Three layers:
 *
 *   1. Eyebrow + heading + a date input (always visible).
 *   2. Mini month calendar — 🟢/🟠/🔴 dots driven by
 *      /api/availability/calendar. Past dates render grey;
 *      arrows nav month-by-month.
 *   3. When a date is selected:
 *        – overall confidence banner (Excellent / Limited /
 *          Fully Booked) sourced from /api/availability?date=.
 *        – per-item availability rows with traffic light + count.
 *        – "Enquire About This Date" CTA → opens EnquiryForm
 *          with the date pre-filled.
 *
 *   When no date is selected, the generic AvailabilityWidget
 *   (date-agnostic, iter223) is rendered as a calm fallback so
 *   the page never looks empty.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import AvailabilityWidget from './AvailabilityWidget';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const SERIF = 'Georgia, "Cormorant Garamond", "Times New Roman", serif';
const GOLD = '#D4AF5E';
const GOLD_FAINT = 'rgba(212,170,82,0.22)';
const GOLD_HAIRLINE = 'rgba(212,170,82,0.14)';
const IVORY = '#FBEED0';
const IVORY_DIM = 'rgba(251,238,208,0.62)';
const IVORY_GHOST = 'rgba(251,238,208,0.42)';

const STATUS_STYLE = {
  green:  { dot: '#7DB58C', border: 'rgba(125,181,140,0.32)', text: '#CFE6CC' },
  amber:  { dot: '#E2C77B', border: 'rgba(226,199,123,0.34)', text: '#F4E2A8' },
  orange: { dot: '#E39A4F', border: 'rgba(227,154,79,0.40)', text: '#F6CC92' },
  red:    { dot: '#B86F6F', border: 'rgba(184,111,111,0.36)', text: '#E6B5B5' },
};

const CONFIDENCE_STYLE = {
  excellent:    { dot: '#7DB58C', label: '🟢 Excellent Availability' },
  limited:      { dot: '#E39A4F', label: '🟠 Limited Availability' },
  fully_booked: { dot: '#B86F6F', label: '🔴 Fully Booked' },
};

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June',
                     'July', 'August', 'September', 'October', 'November', 'December'];
const DAY_HEADERS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

const todayIso = () => new Date().toISOString().slice(0, 10);

const prettyDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
};

const DateAvailabilityChecker = ({ onEnquire }) => {
  const today = useMemo(() => new Date(), []);
  const [cursor, setCursor] = useState({ year: today.getFullYear(), month: today.getMonth() + 1 });
  const [days, setDays] = useState([]);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  const [availability, setAvailability] = useState(null);
  const [availLoading, setAvailLoading] = useState(false);

  const loadCalendar = useCallback(async (y, m) => {
    setCalendarLoading(true);
    try {
      const r = await axios.get(`${API}/availability/calendar`, { params: { year: y, month: m } });
      setDays(r.data.days || []);
    } catch {
      setDays([]);
    } finally {
      setCalendarLoading(false);
    }
  }, []);

  useEffect(() => { loadCalendar(cursor.year, cursor.month); }, [cursor, loadCalendar]);

  const loadDate = useCallback(async (iso) => {
    if (!iso) { setAvailability(null); return; }
    setAvailLoading(true);
    try {
      const r = await axios.get(`${API}/availability`, { params: { date: iso } });
      setAvailability(r.data);
    } catch {
      setAvailability(null);
    } finally {
      setAvailLoading(false);
    }
  }, []);

  const handleSelect = (iso, isPast) => {
    if (isPast) return;
    setSelectedDate(iso);
    loadDate(iso);
  };

  const moveMonth = (delta) => {
    let y = cursor.year, m = cursor.month + delta;
    if (m < 1) { m = 12; y -= 1; }
    if (m > 12) { m = 1; y += 1; }
    if (y < today.getFullYear() || (y === today.getFullYear() && m < today.getMonth() + 1)) return;
    setCursor({ year: y, month: m });
  };

  // Grid padding: align the 1st on Monday.
  const firstWeekdayPad = useMemo(() => {
    const first = new Date(cursor.year, cursor.month - 1, 1);
    // 0=Sun, 1=Mon ... → convert to Mon=0
    return (first.getDay() + 6) % 7;
  }, [cursor]);

  return (
    <div
      data-testid="date-availability-checker"
      style={{
        background: 'rgba(20,12,6,0.55)',
        border: `1px solid ${GOLD_FAINT}`,
        borderRadius: 14,
        padding: 'clamp(24px, 3vw, 36px)',
        maxWidth: 620,
        margin: '0 auto',
        boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
        backdropFilter: 'blur(10px)',
        textAlign: 'left',
      }}
    >
      <div style={{
        fontFamily: SERIF, fontStyle: 'italic',
        fontSize: 11, letterSpacing: '0.28em', textTransform: 'uppercase',
        color: GOLD, marginBottom: 10, textAlign: 'center',
      }}>✦ Check Your Date</div>
      <h3 style={{
        fontFamily: SERIF, fontWeight: 500, fontSize: 'clamp(22px, 3vw, 28px)',
        color: IVORY, margin: '0 0 6px', letterSpacing: '-0.005em', textAlign: 'center',
      }}>See what&apos;s free on your evening</h3>
      <p style={{
        fontFamily: SERIF, fontStyle: 'italic',
        fontSize: 13, color: IVORY_DIM, margin: '0 0 22px',
        textAlign: 'center', lineHeight: 1.55,
      }}>
        Pick a date — we&apos;ll show you exactly which booths and add-ons are still available.
      </p>

      {/* Direct date input — power-user shortcut */}
      <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginBottom: 22, flexWrap: 'wrap' }}>
        <input
          type="date"
          min={todayIso()}
          value={selectedDate}
          onChange={(e) => handleSelect(e.target.value, false)}
          data-testid="date-availability-input"
          style={{
            background: 'rgba(20,12,6,0.78)',
            border: `1px solid ${GOLD_FAINT}`,
            borderRadius: 10,
            color: IVORY, fontFamily: SERIF, fontStyle: 'italic',
            fontSize: 14, padding: '10px 14px', outline: 'none',
            colorScheme: 'dark',
          }}
        />
        {selectedDate && (
          <button
            type="button"
            onClick={() => { setSelectedDate(''); setAvailability(null); }}
            data-testid="date-availability-clear"
            style={{
              background: 'transparent', border: `1px solid ${GOLD_HAIRLINE}`,
              borderRadius: 999, padding: '8px 16px',
              color: IVORY_DIM, fontFamily: SERIF, fontStyle: 'italic',
              fontSize: 12.5, cursor: 'pointer',
            }}
          >Clear ✦</button>
        )}
      </div>

      {/* Mini calendar */}
      <div data-testid="availability-calendar" style={{ marginBottom: selectedDate ? 22 : 4 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <button type="button" onClick={() => moveMonth(-1)} data-testid="calendar-prev"
            style={navBtnStyle}>‹</button>
          <div style={{
            fontFamily: SERIF, fontStyle: 'italic',
            fontSize: 14, color: IVORY, letterSpacing: '0.06em',
          }} data-testid="calendar-month-label">
            {MONTH_NAMES[cursor.month - 1]} {cursor.year}
          </div>
          <button type="button" onClick={() => moveMonth(1)} data-testid="calendar-next"
            style={navBtnStyle}>›</button>
        </div>
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4,
          fontFamily: SERIF, fontStyle: 'italic',
          fontSize: 10, color: IVORY_GHOST, letterSpacing: '0.18em',
          textAlign: 'center', marginBottom: 6,
        }}>
          {DAY_HEADERS.map((h, i) => <div key={i}>{h}</div>)}
        </div>
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4,
          opacity: calendarLoading ? 0.5 : 1, transition: 'opacity 220ms ease',
        }}>
          {Array.from({ length: firstWeekdayPad }).map((_, i) => (
            <div key={`pad-${i}`} style={{ aspectRatio: '1 / 1' }} />
          ))}
          {days.map((d) => {
            const isSelected = d.date === selectedDate;
            const s = STATUS_STYLE[d.status] || STATUS_STYLE.amber;
            return (
              <button
                type="button"
                key={d.date}
                onClick={() => handleSelect(d.date, d.is_past)}
                disabled={d.is_past}
                data-testid={`calendar-day-${d.date}`}
                data-status={d.status}
                aria-label={`${prettyDate(d.date)} — ${d.status}`}
                style={{
                  aspectRatio: '1 / 1',
                  display: 'flex', flexDirection: 'column',
                  alignItems: 'center', justifyContent: 'center',
                  gap: 2,
                  borderRadius: 8,
                  background: isSelected
                    ? 'rgba(212,170,82,0.22)'
                    : d.is_past ? 'transparent' : 'rgba(255,255,255,0.025)',
                  border: `1px solid ${isSelected ? GOLD : 'rgba(255,255,255,0.06)'}`,
                  color: d.is_past ? IVORY_GHOST : IVORY,
                  fontFamily: SERIF, fontSize: 13,
                  cursor: d.is_past ? 'default' : 'pointer',
                  opacity: d.is_past ? 0.35 : 1,
                  transition: 'border-color 200ms ease, background 200ms ease',
                  padding: 0,
                }}
              >
                <span>{d.day}</span>
                {!d.is_past && (
                  <span aria-hidden style={{
                    width: 6, height: 6, borderRadius: 999,
                    background: s.dot,
                    boxShadow: `0 0 6px ${s.dot}88`,
                  }} />
                )}
              </button>
            );
          })}
        </div>

        <div style={{
          marginTop: 12, display: 'flex', gap: 14, justifyContent: 'center',
          flexWrap: 'wrap', fontFamily: SERIF, fontStyle: 'italic',
          fontSize: 11.5, color: IVORY_DIM, letterSpacing: '0.04em',
        }}>
          <LegendDot color={STATUS_STYLE.green.dot}  label="Plenty Available" />
          <LegendDot color={STATUS_STYLE.amber.dot}  label="Limited Availability" />
          <LegendDot color={STATUS_STYLE.red.dot}    label="Fully Booked" />
        </div>
      </div>

      {/* Per-date availability */}
      {selectedDate && (
        <div data-testid="date-availability-detail">
          <DateHeader
            date={selectedDate}
            summary={availability?.summary}
            loading={availLoading}
          />
          {availability?.items && (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {availability.items.map((it) => {
                const s = STATUS_STYLE[it.status] || STATUS_STYLE.amber;
                const isUrgent = it.status === 'orange' || it.status === 'red';
                return (
                  <li
                    key={it.slug}
                    data-testid={`date-availability-row-${it.slug}`}
                    data-status={it.status}
                    style={{
                      display: 'flex', alignItems: 'center',
                      justifyContent: 'space-between', gap: 12,
                      padding: '10px 0',
                      borderTop: `1px solid rgba(255,255,255,0.06)`,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                      <span aria-hidden style={{
                        width: 10, height: 10, borderRadius: 999,
                        background: s.dot, flexShrink: 0,
                        boxShadow: `0 0 12px ${s.dot}88`,
                      }} />
                      <span style={{
                        fontFamily: SERIF, fontSize: 14.5, color: IVORY,
                        lineHeight: 1.35,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>{it.name}</span>
                    </div>
                    <span
                      data-testid={`date-availability-label-${it.slug}`}
                      style={{
                        fontFamily: SERIF,
                        fontStyle: isUrgent ? 'italic' : 'normal',
                        fontWeight: isUrgent ? 600 : 500,
                        fontSize: 12.5,
                        color: s.text,
                        padding: '4px 12px', borderRadius: 999,
                        border: `1px solid ${s.border}`,
                        whiteSpace: 'nowrap', letterSpacing: '0.02em',
                      }}
                    >{it.label}</span>
                  </li>
                );
              })}
            </ul>
          )}

          <button
            type="button"
            onClick={() => onEnquire?.(selectedDate)}
            data-testid="date-availability-enquire"
            disabled={availability?.summary?.status === 'fully_booked'}
            style={{
              marginTop: 22, width: '100%',
              padding: '14px 22px', borderRadius: 999,
              border: `1px solid ${GOLD}`,
              background: availability?.summary?.status === 'fully_booked'
                ? 'rgba(255,255,255,0.04)'
                : 'linear-gradient(135deg, rgba(212,170,82,0.22), rgba(184,151,98,0.12))',
              color: availability?.summary?.status === 'fully_booked' ? IVORY_GHOST : IVORY,
              fontFamily: SERIF, fontSize: 14.5, letterSpacing: '0.06em',
              cursor: availability?.summary?.status === 'fully_booked' ? 'not-allowed' : 'pointer',
            }}
          >
            {availability?.summary?.status === 'fully_booked'
              ? 'Fully booked · Try another date'
              : 'Enquire About This Date ✦'}
          </button>
        </div>
      )}

      {/* Fallback: generic stock widget when no date selected (4a) */}
      {!selectedDate && (
        <div style={{
          marginTop: 26,
          paddingTop: 22,
          borderTop: `1px solid ${GOLD_HAIRLINE}`,
        }}>
          <div style={{
            fontFamily: SERIF, fontStyle: 'italic',
            fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase',
            color: IVORY_GHOST, marginBottom: 14, textAlign: 'center',
          }}>Or browse our overall fleet</div>
          <AvailabilityWidget compact />
        </div>
      )}
    </div>
  );
};

const navBtnStyle = {
  background: 'transparent', border: `1px solid ${GOLD_HAIRLINE}`,
  borderRadius: 999, width: 32, height: 32,
  color: GOLD, fontFamily: SERIF, fontSize: 18, cursor: 'pointer',
  lineHeight: 1, padding: 0,
};

const LegendDot = ({ color, label }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
    <span aria-hidden style={{ width: 7, height: 7, borderRadius: 999, background: color, boxShadow: `0 0 6px ${color}88` }} />
    {label}
  </span>
);

const DateHeader = ({ date, summary, loading }) => {
  const conf = summary ? (CONFIDENCE_STYLE[summary.status] || CONFIDENCE_STYLE.limited) : null;
  return (
    <div style={{ textAlign: 'center', marginBottom: 14 }}>
      <div style={{
        fontFamily: SERIF, fontStyle: 'italic',
        fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase',
        color: GOLD, marginBottom: 6,
      }}>Availability for</div>
      <div
        data-testid="date-availability-heading"
        style={{ fontFamily: SERIF, fontWeight: 500, fontSize: 19, color: IVORY, letterSpacing: '-0.005em' }}>
        {prettyDate(date)}
      </div>
      {loading && (
        <div style={{ fontFamily: SERIF, fontStyle: 'italic', color: IVORY_DIM, fontSize: 12.5, marginTop: 10 }}>
          Checking the schedule…
        </div>
      )}
      {!loading && conf && (
        <div
          data-testid="date-availability-confidence"
          data-status={summary.status}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            marginTop: 12, padding: '6px 16px', borderRadius: 999,
            border: `1px solid ${conf.dot}55`,
            background: `${conf.dot}18`,
            fontFamily: SERIF, fontSize: 13.5, color: IVORY,
            letterSpacing: '0.02em',
          }}
        >
          <span>{conf.label}</span>
        </div>
      )}
      {!loading && summary?.message && (
        <div style={{
          fontFamily: SERIF, fontStyle: 'italic',
          fontSize: 12.5, color: IVORY_DIM,
          marginTop: 8, lineHeight: 1.5, maxWidth: 380, margin: '8px auto 0',
        }}>{summary.message}</div>
      )}
    </div>
  );
};

export default DateAvailabilityChecker;
